import { useMemo, useState, type ReactNode } from 'react';
import { KeyRound, Plus, RefreshCw, Table2, Unplug } from 'lucide-react';
import { Button, DropdownMenuItem, DropdownMenuSeparator } from '@loader/ui';
import type { TableConnector } from '@loader/shared-types';

import { useUpdateCustomSchedule } from '../hooks/useCustomConnectors';
import { useSyncProgress } from '../hooks/useSyncTrigger';
import { IntegrationCard, ConnectorSyncRow, ScheduleMenuItem, resolveEffectiveStatus } from './shared';
import { useScheduleEditDialog } from './useScheduleEditDialog';
import type { AvailableSync } from '../types';

import { ConfirmDeleteDialog } from '@/components/common';
import { useToastStore } from '@/store';
import type { Table } from '@/lib/types';
import type { SyncStatus } from './shared';

// ============================================================================
// Props
// ============================================================================

export interface ConnectionGroupProps {
  /** Icon element (logo image or lucide icon) */
  icon: ReactNode;
  /** Display name for the connection (e.g. "HubSpot (Acme Corp)" or "Stripe") */
  label: string;

  /** Connectors belonging to this connection */
  connectors: TableConnector[];
  /** Tables corresponding to the connectors */
  tables: Table[];

  /** All syncs the provider exposes (null = still loading) */
  availableSyncs: AvailableSync[] | null;
  /** Extract the sync key from a connector's config */
  getSyncKey: (connector: TableConnector) => string | undefined;
  /** Derive normalised SyncStatus for a connector */
  getStatus: (connector: TableConnector, table: Table | undefined) => SyncStatus;

  /** Trigger an immediate sync */
  onTriggerSync: (tableId: string) => void;
  /** Pause syncing */
  onPause: (tableId: string) => void;
  /** Resume syncing */
  onResume: (tableId: string) => void;
  /** Activate a new sync (e.g. create table for an object type) */
  onActivate: (syncKey: string) => void;
  /** Disconnect the entire connection. Returns a promise so the dialog can show loading state. */
  onDisconnect: () => void | Promise<void>;
  /** Re-authenticate to refresh tokens/scopes (OAuth re-flow) */
  onReconnect?: () => void;

  /** Show last-synced timestamp on each row (native integrations) */
  showLastSyncedAt?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ConnectionGroup({
  icon,
  label,
  connectors,
  tables,
  availableSyncs,
  getSyncKey,
  getStatus,
  onTriggerSync,
  onPause,
  onResume,
  onActivate,
  onDisconnect,
  onReconnect,
  showLastSyncedAt = false,
}: ConnectionGroupProps) {
  const updateSchedule = useUpdateCustomSchedule();
  const { openScheduleDialog, scheduleEditDialog } = useScheduleEditDialog(updateSchedule);
  const toast = useToastStore();
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Map: syncKey → { connector, table }
  const activeSyncs = useMemo(() => {
    const map = new Map<string, { connector: TableConnector; table: Table | undefined }>();
    for (const conn of connectors) {
      const key = getSyncKey(conn);
      if (key) {
        map.set(key, {
          connector: conn,
          table: tables.find((t) => t.id === conn.table_id),
        });
      }
    }
    return map;
  }, [connectors, tables, getSyncKey]);

  // Sort alphabetically by label
  const sortedSyncs = useMemo(() => {
    if (!availableSyncs) return null;
    return [...availableSyncs].sort((a, b) => a.label.localeCompare(b.label));
  }, [availableSyncs]);

  const handleSyncAll = () => {
    for (const conn of connectors) {
      if (conn.enabled) onTriggerSync(conn.table_id);
    }
    toast.success('Sync triggered for all active syncs');
  };

  const activeCount = activeSyncs.size;
  const totalCount = availableSyncs?.length ?? activeCount;

  return (
    <>
      <IntegrationCard
        icon={icon}
        label={label}
        subtitle={`${activeCount}/${totalCount} active`}
        headerMenuItems={
          <>
            {connectors.length > 0 && (
              <DropdownMenuItem onClick={handleSyncAll}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Sync All
              </DropdownMenuItem>
            )}
            {onReconnect && (
              <>
                <DropdownMenuItem onClick={onReconnect}>
                  <KeyRound className="mr-2 h-3.5 w-3.5" />
                  Reconnect
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDisconnectOpen(true)}
            >
              <Unplug className="mr-2 h-3.5 w-3.5" />
              Disconnect
            </DropdownMenuItem>
          </>
        }
      >
        {sortedSyncs?.map((sync) => {
          const activated = activeSyncs.get(sync.key);

          if (!activated) {
            return (
              <div
                key={sync.key}
                className="border-border/40 group flex items-center gap-3 border-b px-4 py-2 pl-10 last:border-b-0"
              >
                <Table2 className="text-muted-foreground/40 h-3.5 w-3.5 shrink-0" />
                <span className="text-muted-foreground text-sm">{sync.label}</span>
                <div className="ml-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => onActivate(sync.key)}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Activate
                  </Button>
                </div>
              </div>
            );
          }

          const { connector, table } = activated;
          const status = getStatus(connector, table);

          return (
            <SyncRowWithProgress
              key={sync.key}
              name={sync.label}
              subtitle={table?.name}
              connector={connector}
              status={status}
              showLastSyncedAt={showLastSyncedAt}
              onTriggerSync={onTriggerSync}
              onPause={onPause}
              onResume={onResume}
              openScheduleDialog={openScheduleDialog}
            />
          );
        })}

        {/* Loading fallback: show table names while syncs are loading */}
        {!sortedSyncs &&
          tables.map((table) => (
            <div
              key={table.id}
              className="border-border/40 flex items-center gap-3 border-b px-4 py-2 pl-10 last:border-b-0"
            >
              <span className="text-muted-foreground text-sm">{table.name}</span>
            </div>
          ))}
      </IntegrationCard>

      {scheduleEditDialog}

      <ConfirmDeleteDialog
        open={disconnectOpen}
        onOpenChange={(open) => { if (!disconnecting) setDisconnectOpen(open); }}
        title={`Disconnect ${label}`}
        warningMessage={`All ${activeCount} active sync${activeCount !== 1 ? 's' : ''} and their tables will be permanently deleted.`}
        isLoading={disconnecting}
        onConfirm={async () => {
          setDisconnecting(true);
          try {
            await onDisconnect();
            setDisconnectOpen(false);
          } finally {
            setDisconnecting(false);
          }
        }}
      />
    </>
  );
}

// ============================================================================
// Wrapper — calls useSyncProgress hook per row
// ============================================================================

function SyncRowWithProgress({
  name,
  subtitle,
  connector,
  status,
  showLastSyncedAt,
  onTriggerSync,
  onPause,
  onResume,
  openScheduleDialog,
}: {
  name: string;
  subtitle?: string;
  connector: TableConnector;
  status: SyncStatus;
  showLastSyncedAt: boolean;
  onTriggerSync: (tableId: string) => void;
  onPause: (tableId: string) => void;
  onResume: (tableId: string) => void;
  openScheduleDialog: (tableId: string, schedule: string | null) => void;
}) {
  // Always checks on mount (survives navigation). Polls fast while syncing.
  const { syncing: backendSyncing, progress } = useSyncProgress(connector.table_id, status === 'syncing');

  const effectiveStatus = resolveEffectiveStatus(status, backendSyncing);

  return (
    <ConnectorSyncRow
      name={name}
      subtitle={subtitle}
      tableId={connector.table_id}
      status={effectiveStatus}
      schedule={connector.schedule}
      lastSyncedAt={showLastSyncedAt ? connector.last_synced_at : undefined}
      progress={progress}
      onTriggerSync={() => onTriggerSync(connector.table_id)}
      onPause={() => onPause(connector.table_id)}
      onResume={() => onResume(connector.table_id)}
      extraMenuItems={
        <ScheduleMenuItem
          schedule={connector.schedule}
          onClick={() => openScheduleDialog(connector.table_id, connector.schedule ?? null)}
        />
      }
    />
  );
}
